import { clsx } from "clsx"
import { observer } from "mobx-react-lite"
import React, { useCallback, useRef } from "react"
import { dataInteractiveState } from "../../data-interactive/data-interactive-state"
import { DocumentContainerContext } from "../../hooks/use-document-container-context"
import { useRegisterSection } from "../../hooks/use-section-navigation"
import { useDocumentContent } from "../../hooks/use-document-content"
import { logMessageWithReplacement } from "../../lib/log-message"
import { appState } from "../../models/app-state"
import { isFreeTileRow } from "../../models/document/free-tile-row"
import { isMosaicTileRow } from "../../models/document/mosaic-tile-row"
import { getSharedModelManager } from "../../models/tiles/tile-environment"
import { deleteTileNotification } from "../../models/tiles/tile-notifications"
import { uiState } from "../../models/ui-state"
import { t } from "../../utilities/translation/translate"
import { urlParams } from "../../utilities/url-params"
import { EditAttributeFormulaModal } from "../common/edit-attribute-formula-modal"
import { AttributeDragOverlay } from "../drag-drop/attribute-drag-overlay"
import { PluginAttributeDrag } from "../drag-drop/plugin-attribute-drag"
import { kContainerClass } from "./container-constants"
import { FreeTileRowComponent } from "./free-tile-row"
import { MosaicTileRowComponent } from "./mosaic-tile-row"

import "./container.scss"

export const Container: React.FC = observer(function Container() {
  const documentContent = useDocumentContent()
  const isScrollBehaviorAuto = urlParams.scrollBehavior === "auto"
  // TODO: handle the possibility of multiple rows
  const row = documentContent?.getRowByIndex(0)
  const getTile = useCallback((tileId: string) => documentContent?.getTile(tileId), [documentContent])
  const containerRef = useRef<HTMLElement>(null)
  useRegisterSection("tilearea", containerRef, 2)

  const handleCloseTile = useCallback((tileId: string) => {
    const tile = getTile(tileId)
    documentContent?.applyModelChange(() => {
      const manager = getSharedModelManager(documentContent)
      const sharedModels = manager?.getTileSharedModels(tile?.content)
      sharedModels?.forEach(model => {
        manager?.removeTileSharedModel(tile?.content, model)
      })
      tileId && documentContent?.deleteOrHideTile(tileId)
    }, {
      notify: deleteTileNotification(tile),
      log: logMessageWithReplacement("Close component: %@", {tileType: tile?.content.type}, "component"),
      undoStringKey: "DG.Undo.component.close",
      redoStringKey: "DG.Redo.component.close"
    })
  }, [documentContent, getTile])

  const classes = clsx(kContainerClass, {
                        "hide-toolbar": uiState.hideAppToolbar,
                        "scroll-behavior-auto": isScrollBehaviorAuto })
  return (
    <DocumentContainerContext.Provider value={containerRef}>
      <main
        aria-describedby="tile-area-hint"
        aria-label={t("V3.app.tileArea.ariaLabel")}
        className={classes}
        ref={containerRef}
      >
        <h2 className="codap-visually-hidden">{appState.document.title}</h2>
        <span id="tile-area-hint" className="codap-visually-hidden">{t("V3.app.tileAreaHint")}</span>
        {isMosaicTileRow(row) &&
          <MosaicTileRowComponent row={row} getTile={getTile} onCloseTile={handleCloseTile}/>}
        {isFreeTileRow(row) &&
          <FreeTileRowComponent row={row} getTile={getTile} onCloseTile={handleCloseTile}/>}
        <PluginAttributeDrag />
        <AttributeDragOverlay
          dragIdPrefix="plugin"
          overlayHeight={dataInteractiveState.draggingOverlayHeight}
          overlayWidth={dataInteractiveState.draggingOverlayWidth}
          xOffset={dataInteractiveState.draggingXOffset}
          yOffset={dataInteractiveState.draggingYOffset}
        />
        <EditAttributeFormulaModal />
      </main>
    </DocumentContainerContext.Provider>
  )
})
